import { AppAPI, NumericMetricInfo, TagMetricInfo, TimeSeriesAggregation, TimeSeriesData, TimeSeriesRawData } from '@moderepo/mode-apis';
import { convertCollectionDataToMetricsDataPoints, DataPoint, mergeMetricDataToMultiMetricDataPoints, MultiSeriesDataPoint, Time }
    from '@moderepo/mode-app-components';
import { ExtDispatch, fetchTimeSeriesCollectionData, fetchTimeSeriesCollectionRawData, fetchTimeSeriesData, fetchTimeSeriesRawData,
    selectTimeSeriesCollectionData, selectTimeSeriesCollectionRawData, selectTimeSeriesData, selectTimeSeriesRawData, updateHomeKVStore,
    UserAppDataState, UserAppDataStateAction } from '@moderepo/mode-data-state-user-app';
import { Entity, EntityMetricsDefinition, isNumericMetricInfo, RitzAPI } from '@moderepo/mode-internal-apis';
import { RitzDataState, RitzDataStateAction, updateProjectSettings } from '@moderepo/mode-data-state-ritz';
import { TFunction } from 'react-i18next';
import { DataIntervalDashboardWidgetInputErrors, DashboardWidgetInputErrors, DashboardWidgetLayout, DashboardWidgetSettings, DashboardWidgetInputs,
    HOME_DASHBOARDS_KV_KEY, isHomeDashboardsKeyValuePair, TSDBDashboardWidgetDataSourceInputErrors, DataIntervalDashboardWidgetInputs,
    DataIntervalViewType, AggregatedDataIntervalType, RawDataIntervalType,
    ValueDashboardWidgetInputs, ValueDashboardWidgetInputErrors, DataIntervalDashboardWidgetSettings, FetchRawSimpleSeriesDataParams,
    FetchAggregatedSimpleSeriesDataParams, FetchRawCollectionDataParams, FetchAggregatedCollectionDataParams, isSimpleSeriesDashboardWidgetDataSource,
    isFetchAggregatedSimpleSeriesDataParams, isCollectionDashboardWidgetDataSource, isFetchAggregatedCollectionDataParams,
    isFetchRawSimpleSeriesDataParams, isFetchRawCollectionDataParams, CollectionDashboardWidgetDataSource, SimpleSeriesDashboardWidgetDataSource,
    INTERVAL_VALUE_IN_MS_BY_RAW_DATA_TYPE, INTERVAL_VALUE_IN_MS_BY_AGGREGATED_DATA_TYPE, ValueDashboardWidgetViewSettingsInputErrors,
    DataIntervalDashboardWidgetViewSettingsInputErrors, HeatMapDashboardWidgetInputErrors, HeatMapDashboardWidgetInputs,
    TSDBDashboardWidgetDataSourceInputs, MetricsMetadata, Dashboard, isProjectDashboardsSettings, DashboardWidgetViewSettingsInputs,
    DashboardWidgetViewSettingsInputErrors, DataDashboardWidgetInputs, DataDashboardWidgetInputErrors, TSDBDataSourceType,
    isEntityDashboardWidgetDataSource, EntityTSDBDashboardWidgetDataSource, MetricMetadata } from './dashboardTypes';
import { NoneValue, TypeNone } from '../../utils/utils';
import { getMetricsDefinitionMetricsId } from '../entities';
import { DUMMY_VALUE_NAME } from '../time-series/tsUtils';
import { AlertsDashboardWidgetInputErrors, AlertsDashboardWidgetInputs, EntitiesMapDashboardWidgetInputErrors, EntitiesMapDashboardWidgetInputs }
    from '.';
import { MAX_DECIMAL_PLACES, MIN_DECIMAL_PLACES } from '../../utils/constants';



export interface DashboardLogicApiCallResponse {
    readonly success: boolean;
    readonly errorMessageKey?: string | undefined;
}


/**
 * The params required for making API call to Create/Update/Delete dashboard and dashboard widget. We have 2 places where we store
 * dashboard settings, Home KV for home dashboards and Project settings for Project dashboard. Instead of putting a check everywhere
 * to check if a dashboard is a home dashboard or project dashboard, the only place we need to do this check is when we fetch dashboard
 * settings and when we save it. So when a caller call one of the get/save dashboard functions, it needs to provide the params needed to
 * save the settings.
 */
interface DashboardLogicApiCallParams {
    // ONLY needed if a dashboard is a home dashboard and we need to get/save from/to the Home KV
    readonly home?: {
        readonly dataState: UserAppDataState, dataDispatch: ExtDispatch<UserAppDataStateAction>;
        readonly homeId: number;
    };

    // ONLY needed if a dashboard is a Project dashboard and we need to get/save from/to Project settings
    readonly project?: {
        readonly dataState: RitzDataState, dataDispatch: ExtDispatch<RitzDataStateAction>;
        readonly projectId: number;
        readonly agentId: number;
    };
}


/**
 * Helper function to get the latest dashboards from Home KV or project settings depending on the params.
 *
 * IMPORTANT NOTE: It is possible that multiple agents are updating the dashboard at the same time. There is only 1 Home KV or project settings
 * used for storing the dashboard settings so that mean the agents will be updating the same data. To prevent 1 agent from
 * overriding another agent's changes, we will always fetch the latest dashboards before we update it. We will not use the one we
 * loaded earlier because it could be stale data. This is NOT perfect, there is still a small chance that can happen if 2 agents
 * update at the EXACT same time, but there isn't much we can do about that.
 *
 * @param params
 * @param homeId
 */
const getLatestDashboards = async (params: DashboardLogicApiCallParams): Promise<readonly Dashboard[]> => {
    try {
        if (params.home) {
            const latestDashboardHomeKV = await AppAPI.getInstance().getHomeKeyValuePair(params.home.homeId, HOME_DASHBOARDS_KV_KEY);
            const dashboards = latestDashboardHomeKV && isHomeDashboardsKeyValuePair(latestDashboardHomeKV) ? latestDashboardHomeKV.value : [];
            return dashboards;
        } if (params.project) {
            const projectSettings = await RitzAPI.getInstance().getAgentProjectById(params.project.agentId, params.project.projectId);
            const dashboards = isProjectDashboardsSettings(projectSettings.supportToolSettings.dashboardSettings)
                ? projectSettings.supportToolSettings.dashboardSettings.dashboards : [];
            return dashboards;
        }
    } catch (error) {
        // Do nothing, the KV probably does not exist
    }

    return [];
};



/**
 * Make API call to save the Dashboards array. This will save the dashboard to Home KV or Project settings depending on the params.
 * NOTE: This function WILL NOT fetch tha latest dashboard to check for changes from the other user. It will make API call and replace the
 * dashboards settings provided. Therefore, this should only be called by the functions in this file because those functions already handle
 * fetching the latest dashboards array before making change to make sure we don't override the other user changes.
 *
 * @param params
 * @param newDashboardsArray
 */
const saveDashboards = async (params: DashboardLogicApiCallParams, newDashboardsArray: readonly Dashboard[]) => {
    if (params.home) {
        // Update Home KV
        await params.home.dataDispatch(updateHomeKVStore(params.home.homeId, HOME_DASHBOARDS_KV_KEY, newDashboardsArray), false);
    } else if (params.project) {
        // Update Project Settings
        await params.project.dataDispatch(updateProjectSettings(params.project.projectId, params.project.agentId, {
            dashboardSettings: JSON.stringify({
                dashboards: newDashboardsArray,
            }, undefined, 4),
        }), false);
    }
};


/**
 * PRIVATE Add a dashboard function
 */
const addDashboard = async (params: DashboardLogicApiCallParams, newDashboard: Dashboard): Promise<DashboardLogicApiCallResponse> => {
    // Get the latest list of dashboards from HOME KV and find the index from the dashboards array where the 'newDashboard' object is located
    const dashboards = await getLatestDashboards(params);

    const dashboardIndex = dashboards.findIndex((dashboard) => {
        return dashboard.id === newDashboard.id;
    });

    // If dashboard already exist, return error. We can't add a dashboard that have the same ID
    if (dashboardIndex >= 0) {
        return {
            success        : false,
            errorMessageKey: 'error.dashboard.already_exist',
        };
    }

    try {
        // Save the dashboards. Because 'dashboards' is an array of ALL the dashboards but we are only updating 1 of the dashboards therefore
        // we will pass the entire original 'dashboards' to the backend EXCEPT the 1 dashboard that we updated
        await saveDashboards(params, [
            ...dashboards,      // send the existing dashboards
            newDashboard,       // add the newDashboard to the end
        ]);
    } catch (error) {
        // API call failed. The appDataDispatch already handled the error so we don't need to return the error message, just return
        // success = false is enough
        return {
            success: false,
        };
    }

    // if we get here then that mean dashboard is add successfully
    return {
        success: true,
    };
};



/**
 * Add a dashboard to a HOME
 */
export const addHomeDashboard = async (
    dataState: UserAppDataState, dataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number, newDashboard: Dashboard,
): Promise<DashboardLogicApiCallResponse> => {
    return addDashboard({
        home: { dataState, dataDispatch, homeId },
    }, newDashboard);
};


/**
 * Add a dashboard to a PROJECT
 */
export const addProjectDashboard = async (
    dataState: RitzDataState, dataDispatch: ExtDispatch<RitzDataStateAction>,
    projectId: number, agentId: number, newDashboard: Dashboard,
): Promise<DashboardLogicApiCallResponse> => {
    return addDashboard({
        project: { dataState, dataDispatch, projectId, agentId },
    }, newDashboard);
};



/**
 * PRIVATE Update a dashboard function
 */
const updateDashboard = async (
    params: DashboardLogicApiCallParams,
    dashboardToUpdate: Dashboard,
): Promise<DashboardLogicApiCallResponse> => {

    // Get the latest list of dashboards from HOME KV and find the index from the dashboards array where the 'dashboardToUpdate' object is located
    const dashboards = await getLatestDashboards(params);
    const dashboardIndex = dashboards.findIndex((dashboard) => {
        return dashboard.id === dashboardToUpdate.id;
    });

    // If dashboard does not exist, return error. We can't update a dashboard in a dashboard array that does not exist
    if (dashboardIndex < 0) {
        return {
            success        : false,
            errorMessageKey: 'error.dashboard.not_exist',
        };
    }


    try {
        // Save the dashboards. Because 'dashboards' is an array of ALL the dashboards but we are only updating 1 of the dashboards therefore
        // we will pass the entire original 'dashboards' to the backend EXCEPT the 1 dashboard that we updated
        await saveDashboards(params, dashboards.map((currentDashboard) => {
            // if the currentDashboard is the same as the one we are updating then return the dashboardToUpdate
            if (currentDashboard.id === dashboardToUpdate.id) {
                return dashboardToUpdate;
            }

            // else return the same dashboard, this one is not changed
            return currentDashboard;
        }));
    } catch (error) {
        // API call failed. The appDataDispatch already handled the error so we don't need to return the error message, just return
        // success = false is enough
        return {
            success: false,
        };
    }

    // if we get here then that mean dashboard is updated successfully
    return {
        success: true,
    };
};


/**
 * Update an existing dashboard in a HOME
 */
export const updateHomeDashboard = async (
    dataState: UserAppDataState, dataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number, dashboardToUpdate: Dashboard,
): Promise<DashboardLogicApiCallResponse> => {
    return updateDashboard({
        home: { dataState, dataDispatch, homeId },
    }, dashboardToUpdate);
};



/**
 * Update an existing dashboard in a PROJECT
 */
export const updateProjectDashboard = async (
    dataState: RitzDataState, dataDispatch: ExtDispatch<RitzDataStateAction>,
    projectId: number, agentId: number, dashboardToUpdate: Dashboard,
): Promise<DashboardLogicApiCallResponse> => {
    return updateDashboard({
        project: { dataState, dataDispatch, projectId, agentId },
    }, dashboardToUpdate);
};



/**
 * PRIVATE Move a dashboard forward/backward by some index
 */
const moveDashboard = async (
    params: DashboardLogicApiCallParams,
    dashboardToUpdate: Dashboard,
    diff: number,
): Promise<DashboardLogicApiCallResponse> => {

    // Get the latest list of dashboards from HOME KV and find the index from the dashboards array where the 'dashboardToUpdate' object is located
    const dashboards = await getLatestDashboards(params);
    const dashboardIndex = dashboards.findIndex((dashboard) => {
        return dashboard.id === dashboardToUpdate.id;
    });

    // If dashboard does not exist, return error. We can't update a dashboard in a dashboard array that does not exist
    if (dashboardIndex < 0) {
        return {
            success        : false,
            errorMessageKey: 'error.dashboard.not_exist',
        };
    }


    try {
        // only need to move the dashboard forward by 1 index if it is NOT the FIRST dashboard
        if (dashboardIndex + diff >= 0 && dashboardIndex + diff < dashboards.length) {
            // Make a copy of the dashboards array and swap the elements at dashboardIndex and dashboardIndex - 1
            const newDashboardsArray = [
                ...dashboards,
            ];
            const temp = newDashboardsArray[dashboardIndex];
            newDashboardsArray[dashboardIndex] = newDashboardsArray[dashboardIndex + diff];
            newDashboardsArray[dashboardIndex + diff] = temp;

            await saveDashboards(params, newDashboardsArray);
        }
    } catch (error) {
        // API call failed. The appDataDispatch already handled the error so we don't need to return the error message, just return
        // success = false is enough
        return {
            success: false,
        };
    }

    // if we get here then that mean dashboard is updated successfully
    return {
        success: true,
    };
};


/**
 * Move a HOME dashboard forward by 1 index
 */
export const moveHomeDashboardForward = async (
    dataState: UserAppDataState, dataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number, dashboardToUpdate: Dashboard,
): Promise<DashboardLogicApiCallResponse> => {
    return moveDashboard({
        home: { dataState, dataDispatch, homeId },
    }, dashboardToUpdate, -1);
};


/**
 * Move a PROJECT dashboard forward by 1 index
 */
export const moveProjectDashboardForward = async (
    dataState: RitzDataState, dataDispatch: ExtDispatch<RitzDataStateAction>,
    projectId: number, agentId: number, dashboardToUpdate: Dashboard,
): Promise<DashboardLogicApiCallResponse> => {
    return moveDashboard({
        project: { dataState, dataDispatch, projectId, agentId },
    }, dashboardToUpdate, -1);
};


/**
 * Move a HOME dashboard backward by 1 index
 */
export const moveHomeDashboardBackward = async (
    dataState: UserAppDataState, dataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number, dashboardToUpdate: Dashboard,
): Promise<DashboardLogicApiCallResponse> => {
    return moveDashboard({
        home: { dataState, dataDispatch, homeId },
    },
    dashboardToUpdate, 1);
};


/**
 * Move a PROJECT dashboard backward by 1 index
 */
export const moveProjectDashboardBackward = async (
    dataState: RitzDataState, dataDispatch: ExtDispatch<RitzDataStateAction>,
    projectId: number, agentId: number, dashboardToUpdate: Dashboard,
): Promise<DashboardLogicApiCallResponse> => {
    return moveDashboard({
        project: { dataState, dataDispatch, projectId, agentId },
    }, dashboardToUpdate, 1);
};


/**
 * PRIVATE Delete a dashboard forward function
 */
const deleteDashboard = async (
    params: DashboardLogicApiCallParams, dashboardToBeDeletedId: number,
): Promise<DashboardLogicApiCallResponse> => {
    // Get the latest list of dashboards from HOME KV and find the index from the dashboards array where the 'dashboardId' object is located
    const dashboards = await getLatestDashboards(params);
    const dashboardIndex = dashboards.findIndex((dashboard) => {
        return dashboard.id === dashboardToBeDeletedId;
    });

    // If dashboard does not exist, return error. We can't delete a dashboard that does not exist
    if (dashboardIndex < 0) {
        return {
            success        : false,
            errorMessageKey: 'error.dashboard.not_exist',
        };
    }


    try {
        // Save the dashboards by filter out the dashboard to be deleted from the list of dashboards
        await saveDashboards(params, dashboards.filter((currentDashboard) => {
            return currentDashboard.id !== dashboardToBeDeletedId;
        }));
    } catch (error) {
        // API call failed. The appDataDispatch already handled the error so we don't need to return the error message, just return
        // success = false is enough
        return {
            success: false,
        };
    }

    // if we get here then that mean dashboard is updated successfully
    return {
        success: true,
    };
};



/**
 * Delete a dashboard from a HOME
 */
export const deleteHomeDashboard = async (
    dataState: UserAppDataState, dataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number, dashboardToBeDeletedId: number,
): Promise<DashboardLogicApiCallResponse> => {
    return deleteDashboard({
        home: { dataState, dataDispatch, homeId },
    }, dashboardToBeDeletedId);
};


/**
 * Delete a dashboard from a PROJECT
 */
export const deleteProjectDashboard = async (
    dataState: RitzDataState, dataDispatch: ExtDispatch<RitzDataStateAction>,
    projectId: number, agentId: number, dashboardToBeDeletedId: number,
): Promise<DashboardLogicApiCallResponse> => {
    return deleteDashboard({
        project: { dataState, dataDispatch, projectId, agentId },
    }, dashboardToBeDeletedId);
};



/**
 * PRIVATE create a new dashboard widget function
 */
const createNewDashboardWidget = async (
    params: DashboardLogicApiCallParams, dashboardId: number, newWidget: DashboardWidgetSettings,
): Promise<DashboardLogicApiCallResponse> => {

    // Add the new widget to the widgets array and make an API call to save the new homeKV
    try {
        // Get the latest list of dashboards from HOME KV and find the dashboard with the given dashboardId
        const dashboards = await getLatestDashboards(params);
        const dashboardIndex = dashboards.findIndex((dashboard) => {
            return dashboard.id === dashboardId;
        });
        const dashboard = dashboardIndex >= 0 ? dashboards[dashboardIndex] : undefined;

        // If dashboard does not exist, return error. We can't add a widget to a dashboard that does not exist
        if (!dashboard) {
            return {
                success        : false,
                errorMessageKey: 'error.dashboard.not_exist',
            };
        }

        // Create a new dashboard by copying the 'dashboard' and add the newWidget to the dashboard.widgets list
        const newDashboard: Dashboard = {
            ...dashboard,
            widgets: [
                ...(dashboard.widgets || []),
                newWidget,
            ],
        };


        await saveDashboards(params, dashboards.map((currentDashboard) => {
            // if the currentDashboard is the same as the one we are updating then return the newDashboard
            if (currentDashboard.id === newDashboard.id) {
                return newDashboard;
            }

            // else return the same dashboard, this one is not changed
            return currentDashboard;
        }));
    } catch (error) {
        // API call failed. The appDataDispatch already handled the error so we don't need to return the error message, just return
        // success = false is enough
        return {
            success: false,
        };
    }

    return {
        success: true,
    };
};


/**
 * Add a new widget to a Home dashboard
 */
export const createNewHomeDashboardWidget = async (
    dataState: UserAppDataState, dataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number, dashboardId: number, newWidget: DashboardWidgetSettings,
): Promise<DashboardLogicApiCallResponse> => {
    return createNewDashboardWidget({
        home: { dataState, dataDispatch, homeId },
    }, dashboardId, newWidget);
};



/**
 * Add a new widget to a Project dashboard
 */
export const createNewProjectDashboardWidget = async (
    dataState: RitzDataState, dataDispatch: ExtDispatch<RitzDataStateAction>,
    projectId: number, agentId: number, dashboardId: number, newWidget: DashboardWidgetSettings,
): Promise<DashboardLogicApiCallResponse> => {
    return createNewDashboardWidget({
        project: { dataState, dataDispatch, projectId, agentId },
    }, dashboardId, newWidget);
};



/**
 * PRIVATE update dashboard widget function
 */
const updateDashboardWidget = async (
    params: DashboardLogicApiCallParams, dashboardId: number, widgetToUpdate: DashboardWidgetSettings,
): Promise<DashboardLogicApiCallResponse> => {

    // Get the latest list of dashboards from HOME KV and find the dashboard with the given dashboardId
    const dashboards = await getLatestDashboards(params);
    const dashboardIndex = dashboards.findIndex((dashboard) => {
        return dashboard.id === dashboardId;
    });
    const dashboard = dashboardIndex >= 0 ? dashboards[dashboardIndex] : undefined;

    // If dashboard does not exist, return error. We can't update a widget in a dashboard that does not exist
    if (!dashboard) {
        return {
            success        : false,
            errorMessageKey: 'error.dashboard.not_exist',
        };
    }


    try {
        // Find the widgetToUpdate in the widgets array and replace the widget in the array with the widgetToUpdate
        const index = dashboard.widgets?.findIndex((widget) => {
            return widget.id === widgetToUpdate.id;
        });

        if (index !== undefined && index >= 0 && dashboard.widgets) {
            // Make a copy of the 'widgets' because it is not mutable and then change the widget at index 'index' with widgetToUpdate
            const newWidgetsArray = [...dashboard.widgets];
            newWidgetsArray[index] = widgetToUpdate;

            // Create a new dashboard by copying the 'dashboard' and replace the dashboard.widgets array with the new widgets array
            const newDashboard: Dashboard = {
                ...dashboard,
                widgets: newWidgetsArray,
            };

            // Save the dashboards. Because 'dashboards' is an array of ALL the dashboards but we are only updating 1 of the dashboards therefore
            // we will pass the entire original 'dashboards' to the backend EXCEPT the 1 dashboard that we updated
            await saveDashboards(params, dashboards.map((currentDashboard) => {
                // if the currentDashboard is the same as the one we are updating then return the newDashboard
                if (currentDashboard.id === newDashboard.id) {
                    return newDashboard;
                }

                // else return the same dashboard, this one is not changed
                return currentDashboard;
            }));
        } else {
            // If the widgetToUpdate doesn't exist in the latest widgets anymore, it is probably deleted by another agent from another
            // device. Throw an error
            return {
                success        : false,
                errorMessageKey: 'error.dashboard_widget.not_exist',
            };
        }
    } catch (error) {
        // API call failed. The appDataDispatch already handled the error so we don't need to return the error message, just return
        // success = false is enough
        return {
            success: false,
        };
    }

    return {
        success: true,
    };
};



/**
 * Update an existing widget in a HOME dashboard
 */
export const updateHomeDashboardWidget = async (
    dataState: UserAppDataState, dataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number, dashboardId: number, widgetToUpdate: DashboardWidgetSettings,
): Promise<DashboardLogicApiCallResponse> => {
    return updateDashboardWidget({
        home: { dataState, dataDispatch, homeId },
    }, dashboardId, widgetToUpdate);
};



/**
 * Update an existing widget in a PROJECT dashboard
 */
export const updateProjectDashboardWidget = async (
    dataState: RitzDataState, dataDispatch: ExtDispatch<RitzDataStateAction>,
    projectId: number, agentId: number, dashboardId: number, widgetToUpdate: DashboardWidgetSettings,
): Promise<DashboardLogicApiCallResponse> => {
    return updateDashboardWidget({
        project: { dataState, dataDispatch, projectId, agentId },
    }, dashboardId, widgetToUpdate);
};



/**
 * PRIVATE Remove a widget from a dashboard
 */
const removeDashboardWidget = async (
    params: DashboardLogicApiCallParams, dashboardId: number, widgetId: number,
): Promise<DashboardLogicApiCallResponse> => {

    // Get the latest list of dashboards from HOME KV and find the dashboard with the given dashboardId
    const dashboards = await getLatestDashboards(params);
    const dashboardIndex = dashboards.findIndex((dashboard) => {
        return dashboard.id === dashboardId;
    });
    const dashboard = dashboardIndex >= 0 ? dashboards[dashboardIndex] : undefined;

    // If dashboard does not exist, return error. We can't update a widget in a dashboard that does not exist
    if (!dashboard) {
        return {
            success        : false,
            errorMessageKey: 'error.dashboard.not_exist',
        };
    }


    try {
        if (dashboard.widgets && dashboard.widgets.length > 0) {

            // Create a new dashboard by copying the 'dashboard' and filter out the widget we want to delete from the dashboard.widgets
            const newDashboard: Dashboard = {
                ...dashboard,
                widgets: dashboard.widgets.filter((existingWidget) => {
                    // filter out the existingWidget that has the same ID as the widgetToRemove
                    return existingWidget.id !== widgetId;
                }),
            };

            await saveDashboards(params, dashboards.map((currentDashboard) => {
                // if the currentDashboard is the same as the one we are updating then return the newDashboard
                if (currentDashboard.id === newDashboard.id) {
                    return newDashboard;
                }

                // else return the same dashboard, this one is not changed
                return currentDashboard;
            }));
        }
    } catch (error) {
        // API call failed. The appDataDispatch already handled the error so we don't need to return the error message, just return
        // success = false is enough
        return {
            success: false,
        };
    }

    return {
        success: true,
    };
};


/**
 * Remove a widget from a HOME dashboard
 */
export const removeHomeDashboardWidget = async (
    dataState: UserAppDataState, dataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number, dashboardId: number, widgetId: number,
): Promise<DashboardLogicApiCallResponse> => {
    return removeDashboardWidget({
        home: { dataState, dataDispatch, homeId },
    }, dashboardId, widgetId);
};



/**
 * Remove a widget from a PROJECT dashboard
 */
export const removeProjectDashboardWidget = async (
    dataState: RitzDataState, dataDispatch: ExtDispatch<RitzDataStateAction>,
    projectId: number, agentId: number, dashboardId: number, widgetId: number,
): Promise<DashboardLogicApiCallResponse> => {
    return removeDashboardWidget({
        project: { dataState, dataDispatch, projectId, agentId },
    }, dashboardId, widgetId);
};



/**
 * PRIVATE function to Update a dashboard widget layout
 */
const updateWidgetsLayouts = async (
    params: DashboardLogicApiCallParams,
    dashboardId: number,
    layoutsByWidgetId: {
        [widgetId: string]: {
            [screenSize: string]: DashboardWidgetLayout | undefined
        } | undefined
    },
): Promise<DashboardLogicApiCallResponse> => {

    // Get the latest list of dashboards from HOME KV and find the dashboard with the given dashboardId
    const dashboards = await getLatestDashboards(params);
    const dashboardIndex = dashboards.findIndex((dashboard) => {
        return dashboard.id === dashboardId;
    });
    const dashboard = dashboardIndex >= 0 ? dashboards[dashboardIndex] : undefined;

    // If dashboard does not exist, return error. We can't update a widget in a dashboard that does not exist
    if (!dashboard) {
        return {
            success        : false,
            errorMessageKey: 'error.dashboard.not_exist',
        };
    }

    try {
        // GO through EACH widget in the widgets array and update their layout
        const updatedWidgets = dashboard.widgets?.map((widget): DashboardWidgetSettings => {
            // find the new layout for the current widget
            const layouts = layoutsByWidgetId[widget.id.toString()];

            if (layouts && layouts.lg) {
                // only update widget layouts if it is not null/undefined
                return {
                    ...widget,
                    layouts: {
                        lg: layouts.lg,
                    },
                };
            }

            // return the same widget
            return widget;
        });

        // Create a new dashboard by copying the 'dashboard' and replace the dashboard.widgets with the updatedWidgets list
        const newDashboard: Dashboard = {
            ...dashboard,
            widgets: updatedWidgets,
        };

        await saveDashboards(params, dashboards.map((currentDashboard) => {
            // if the currentDashboard is the same as the one we are updating then return the newDashboard
            if (currentDashboard.id === newDashboard.id) {
                return newDashboard;
            }

            // else return the same dashboard, this one is not changed
            return currentDashboard;
        }));
    } catch (error) {
        // API call failed. The appDataDispatch already handled the error so we don't need to return the error message, just return
        // success = false is enough
        return {
            success: false,
        };
    }

    return {
        success: true,
    };
};


/**
 * Update a HOME dashboard widget's layout
 */
export const updateHomeWidgetsLayouts = async (
    dataState: UserAppDataState, dataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number,
    dashboardId: number,
    layoutsByWidgetId: {
        [widgetId: string]: {
            [screenSize: string]: DashboardWidgetLayout | undefined
        } | undefined
    },
): Promise<DashboardLogicApiCallResponse> => {
    return updateWidgetsLayouts({
        home: { dataState, dataDispatch, homeId },
    }, dashboardId, layoutsByWidgetId);
};


/**
 * Update a PROJECT dashboard widget's layout
 */
export const updateProjectWidgetsLayouts = async (
    dataState: RitzDataState, dataDispatch: ExtDispatch<RitzDataStateAction>,
    projectId: number,
    agentId: number,
    dashboardId: number,
    layoutsByWidgetId: {
        [widgetId: string]: {
            [screenSize: string]: DashboardWidgetLayout | undefined
        } | undefined
    },
): Promise<DashboardLogicApiCallResponse> => {
    return updateWidgetsLayouts({
        project: { dataState, dataDispatch, projectId, agentId },
    }, dashboardId, layoutsByWidgetId);
};



/**
 * This is the structure of the data we need to display the DashboardWidget that has time series data as dataSource.
 * Once we loaded simple time series or collection data, we will reformat them and put them into this structure.
 */
export interface TSDashboardWidgetRequiredViewData {
    readonly numericMetrics?: readonly NumericMetricInfo[] | undefined;
    readonly numericMetricsData?: MultiSeriesDataPoint<number>[] | undefined;
    readonly tagMetrics?: readonly TagMetricInfo[] | undefined;
    readonly tagMetricsData?: MultiSeriesDataPoint<string>[] | undefined;
    readonly dataRange: {
        readonly start: number;
        readonly end: number;
    }
}


/**
 * Fetch simple series data
 */
export const fetchSimpleSeriesData = async (
    appDataState: UserAppDataState,
    appDataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number,
    smartModuleId: string,
    seriesIds: readonly string[],
    aggregation: TimeSeriesAggregation | TypeNone,
    startTime: string,
    endTime: string,
    limit: number = 0,
): Promise<FetchAggregatedSimpleSeriesDataParams | FetchRawSimpleSeriesDataParams> => {

    // Because we need to fetch multiple time series, we need to create all the dispatch actions first and then execute them in 1 promiseAll.
    // We also need to keep track of the failed/succeed actions so that we know which API call failed and not wait for the data for that series
    const fetchActions = seriesIds.map((seriesId) => {


        if (aggregation === NoneValue) {
            // fetch raw data. But first check the data state to see if data already existed for the given params
            const existingData = selectTimeSeriesRawData(appDataState, homeId, smartModuleId, seriesId, endTime, limit);

            if (!existingData) {
                return appDataDispatch(fetchTimeSeriesRawData(homeId, smartModuleId, seriesId, endTime, limit), false, true);
            }

            // Data already exist, return success promise
            return Promise.resolve();
        }

        // fetch aggregated data. But first check the data state to see if data already existed for the given params
        const existingData = selectTimeSeriesData(appDataState, homeId, smartModuleId, seriesId, startTime, endTime, aggregation);

        if (!existingData) {
            return appDataDispatch(fetchTimeSeriesData(homeId, smartModuleId, seriesId,
                startTime, (new Date(endTime)).toISOString(), aggregation), false, true);
        }

        // Data already exist, return success promise
        return Promise.resolve();
    });


    // Wait for all the fetch data action to be done and compile a list of time series id that was fetched successfully.
    const succeedTimeSeriesIds = await Promise.allSettled(fetchActions)
        .then((allResults) => {
            return allResults.map((singleResult, index: number) => {
                if (singleResult.status === 'fulfilled') {
                    return seriesIds[index];
                }
                return undefined;
            }).reduce((result: string[], seriesId: string | undefined) => {
                if (seriesId) {
                    return [...result, seriesId];
                }
                return result;
            }, []);
        });


    // Return an object containing the params we used when fetching data
    if (aggregation === NoneValue) {
        // We are fetching RAW data so return a FetchRawSimpleSeriesDataParams
        return {
            homeId, smartModuleId, seriesIds, endTime, limit, succeedTimeSeriesIds,
        };
    }

    // We are fetching AGGREGATED data so return a FetchAggregatedSimpleSeriesDataParams
    return {
        homeId, smartModuleId, seriesIds, aggregation, startTime, endTime, succeedTimeSeriesIds,
    };
};



/**
 * Fetch collection data
 */
export const fetchCollectionData = async (
    appDataState: UserAppDataState,
    appDataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number,
    smartModuleId: string,
    collectionId: string,
    valueNames: readonly string[] | undefined,
    tagNames: readonly string[] | undefined,
    aggregation: TimeSeriesAggregation | TypeNone,
    startTime: string,
    endTime: string,
    limit: number = 0,
): Promise<FetchAggregatedCollectionDataParams | FetchRawCollectionDataParams> => {
    // backend does not allow fetching collection data without at least 1 value name. Therefore if the user didn't select any value name
    // for the widget, we have to pass some dummy value name to the backend just to get around this limitation.
    const fixedValueNames = valueNames && valueNames.length > 0 ? valueNames : [DUMMY_VALUE_NAME];

    if (aggregation === NoneValue) {
        // fetch RAW data and return FetchRawCollectionDataParams but first try to see if the data already existed in the data state based
        // on the fetch params. If it is then don't need to dispatch any action to fetch data
        const existingData = selectTimeSeriesCollectionRawData(
            appDataState, homeId, smartModuleId, collectionId, endTime, limit, fixedValueNames, tagNames || [],
        );

        if (!existingData) {
            appDataDispatch(
                fetchTimeSeriesCollectionRawData(homeId, smartModuleId, collectionId, endTime, limit, fixedValueNames, tagNames || []), false, true,
            );
        }

        // Save the params we used for fetching the data
        return {
            homeId, smartModuleId, collectionId, endTime, limit, valueNames, tagNames,
        };
    }

    // Fetch AGGREGATED data and return FetchAggregatedCollectionDataParams. But first, try to see if the data already existed in the data
    // state based on the fetch params. If it is then don't need to dispatch any action to fetch data
    const existingData = selectTimeSeriesCollectionData(
        appDataState, homeId, smartModuleId, collectionId, startTime, endTime, fixedValueNames, aggregation,
    );

    if (!existingData) {
        appDataDispatch(
            fetchTimeSeriesCollectionData(homeId, smartModuleId, collectionId, startTime, endTime, fixedValueNames, aggregation), false, true,
        );
    }

    return {
        homeId, smartModuleId, collectionId, startTime, endTime, valueNames: valueNames || [], aggregation,
    };
};



/**
 * This function will be the main function to fetch data. However, it doesn't have to logic to fetch data, it will call the appropriate
 * function to fetch data based on the widget's dataSource settings
 */
export const fetchTSData = async (
    appDataState: UserAppDataState, appDataDispatch: ExtDispatch<UserAppDataStateAction>,
    homeId: number, dataSource: DataIntervalDashboardWidgetSettings['dataSource'],
    entity?: Entity | undefined, metricsDefinition?: EntityMetricsDefinition | undefined,
): Promise<
FetchAggregatedSimpleSeriesDataParams | FetchRawSimpleSeriesDataParams | FetchAggregatedCollectionDataParams | FetchRawCollectionDataParams
| undefined
> => {

    // The last data point of the data we need to fetch is NOW rounded down to the closest minute and ignore the seconds and milliseconds.
    // This is for optimization so that we don't fetch data again when the user switch between views quickly.
    const now = new Date();
    const endTime = (new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), 0)).valueOf();

    // If we need to fetch aggregated data, we need to fetch by start/end time. We need to convert the widget's intervalType, e.g. 'Last 1 day',
    // 'Last 30 days', etc..., to the # of milliseconds and subtract endTime by that interval value to get the startTime
    const startTime = dataSource.aggregationType !== NoneValue
        ? endTime - (INTERVAL_VALUE_IN_MS_BY_AGGREGATED_DATA_TYPE[dataSource.intervalType] || Time.DAY_IN_MS)
        : endTime;  // startTime will not be used RAW data so set it to the same as endTime is fine, it won't matter.

    // If we need to fetch raw data when we need to fetch by endTime and limit. NOTE that limit will be a negative number because we
    // want to fetch the last N values starting from now. The way to fetch the last N number if to pass it a -N value.
    const limit = dataSource.aggregationType === NoneValue
        ? -(INTERVAL_VALUE_IN_MS_BY_RAW_DATA_TYPE[dataSource.intervalType] || 100)
        : -1;   // limit won't be used by AGGREGATED data so just give it any number is fine

    if (isSimpleSeriesDashboardWidgetDataSource(dataSource)) {
        return fetchSimpleSeriesData(
            appDataState, appDataDispatch, homeId, dataSource.smartModuleId, dataSource.seriesIds, dataSource.aggregationType,
            (new Date(startTime)).toISOString(), (new Date(endTime)).toISOString(), limit,
        );
    }
    if (isCollectionDashboardWidgetDataSource(dataSource)) {
        return fetchCollectionData(
            appDataState, appDataDispatch,
            homeId, dataSource.smartModuleId, dataSource.collectionId, dataSource.valueNames, dataSource.tagNames,
            dataSource.aggregationType, (new Date(startTime)).toISOString(), (new Date(endTime)).toISOString(), limit,
        );
    }
    if (isEntityDashboardWidgetDataSource(dataSource) && entity && metricsDefinition) {
        const collectionId = getMetricsDefinitionMetricsId(entity, metricsDefinition.metricsDefinitionId);
        const smartModuleId = metricsDefinition.dataStoreConfiguration.moduleInstanceId;
        return fetchCollectionData(
            appDataState, appDataDispatch,
            homeId, smartModuleId, collectionId, dataSource.valueNames, dataSource.tagNames,
            dataSource.aggregationType, (new Date(startTime)).toISOString(), (new Date(endTime)).toISOString(), limit,
        );
    }

    return undefined;
};



/**
 * For EACH metric name, temperature, pressure, in the given metricsData, check if there is a conversion formula that we need to apply to the
 * metric. If there is, we apply the conversion and create a new array of data points for that metric.
 * The data structure of the metricsData is like this
 *
 *      {
 *          temperature: [{date:"2021-03-25T07:00:00Z", value:1}, {date:"2021-03-25T08:00:00Z", value:5}, ...],
 *          pressure: [{date:"2021-03-25T07:00:00Z", value:10}, {date:"2021-03-25T08:00:00Z", value:34}, ...],
 *          humidity: [{date:"2021-03-25T07:00:00Z", value:23}, {date:"2021-03-25T08:00:00Z", value:45}, ...],
 *          ...
 *      }
 * @param metricsData
 * @param metricsMetadata
 * @returns
 */
const applyConversionFormula = (
    metricsData: {
        [metricName: string]: readonly DataPoint<number>[]
    },
    metricsMetadata: MetricsMetadata | undefined,
): {
    [metricName: string]: readonly DataPoint<number>[]
} => {

    // If there is no metadata at all then we don't need to apply conversion formula, just return the same data
    if (!metricsMetadata) {
        return metricsData;
    }

    // Get all the keys from the metricsData, these will be the names of the metrics
    const metricNames = Object.keys(metricsData);

    const convertedMetricsData = metricNames.reduce((result, metricName) => {

        // Check if the metric metadata has conversionFormula set
        const metricConversionFormula = metricsMetadata[metricName]?.conversionFormula;
        if (metricConversionFormula) {

            // Create a Function using the conversionFormula string as body
            // eslint-disable-next-line no-new-func
            const conversionFunction = new Function('value', `return (${metricConversionFormula})`);

            // For each data point in the array, call the conversionFunction and pass it the data point's value
            // and use the returned value as the value for the new data point.
            const originalDataPointsArray = metricsData[metricName];
            const newDataPointsArray = originalDataPointsArray.map((dataPoint): DataPoint<number> => {
                try {
                    // Because the conversionFormula can be anything, we need to use try/catch just incase there is some problem.
                    return {
                        ...dataPoint,
                        value: dataPoint !== null ? conversionFunction(dataPoint.value) : null,
                    };
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error(error);

                    // If calling the conversionFunction failed, set the value to null
                    return {
                        ...dataPoint,
                        value: null,
                    };
                }
            });

            // The current metric doesn't have conversionFormula set so just return the same array of data points.
            return {
                ...result,
                [metricName]: newDataPointsArray,
            };
        }
        return {
            ...result,
            [metricName]: metricsData[metricName],
        };
    }, {});

    return convertedMetricsData;
};



/**
 * Select SIMPLE SERIES data from the data state and use the provided fetchDataParams to find the data
 */
export const selectSimpleSeriesData = (
    appDataState: UserAppDataState,
    fetchDataParams: FetchAggregatedSimpleSeriesDataParams | FetchRawSimpleSeriesDataParams,
    dataSource: SimpleSeriesDashboardWidgetDataSource,
): TSDashboardWidgetRequiredViewData | undefined => {

    // NOTE: select the data based on the succeedTimeSeriesIds not based on the widget's dataSource.seriesId because dataSource.seriesId
    // might contains series that have no longer available/exist.
    const multipleSeriesData = fetchDataParams.succeedTimeSeriesIds.map((seriesId): TimeSeriesData | TimeSeriesRawData | undefined => {
        if (isFetchAggregatedSimpleSeriesDataParams(fetchDataParams)) {
            return selectTimeSeriesData(
                appDataState,
                fetchDataParams.homeId,
                fetchDataParams.smartModuleId,
                seriesId,
                fetchDataParams.startTime,
                fetchDataParams.endTime,
                fetchDataParams.aggregation,
            );
        }
        return selectTimeSeriesRawData(
            appDataState,
            fetchDataParams.homeId,
            fetchDataParams.smartModuleId,
            seriesId,
            fetchDataParams.endTime,
            fetchDataParams.limit,
        );
    }).reduce((result: readonly (TimeSeriesData | TimeSeriesRawData)[], data: TimeSeriesData | TimeSeriesRawData| undefined) => {
        if (data !== undefined) {
            return [...result, data];
        }
        return result;
    }, []);


    // If the length of the time series data we selected is not the same as the length of the succeedTimeSeriesIds then that mean the data is
    // not ready yet. Return undefined for now and wait for the data state to be updated and it will call this function again.
    if (multipleSeriesData.length < fetchDataParams.succeedTimeSeriesIds.length) {
        return undefined;
    }


    /**
     * Each element in the multipleSeriesData contain data for only 1 measurement. We will combine them into 1 object to make them easier
     * to work with, just like Collection data format.
     *      {
     *          temperature: [{date:"2021-03-25T07:00:00Z", value:1}, {date:"2021-03-25T08:00:00Z", value:5}, ...],
     *          pressure: [{date:"2021-03-25T07:00:00Z", value:10}, {date:"2021-03-25T08:00:00Z", value:34}, ...],
     *          humidity: [{date:"2021-03-25T07:00:00Z", value:23}, {date:"2021-03-25T08:00:00Z", value:45}, ...],
     *          ...
     *      }
    */
    const combinedTimeSeriesData = multipleSeriesData.reduce((
        result: {
            [seriesId: string]: readonly DataPoint<number>[]
        },
        seriesData: TimeSeriesData | TimeSeriesRawData,
    ) => {
        return {
            ...result,
            [seriesData.seriesId]: seriesData.data.map(([date, value]): DataPoint<number> => {
                return {
                    dateString: date, dateValue: Date.parse(date), value,
                };
            }),
        };
    }, {});


    // Call the helper function to apply conversion formula to the numeric metrics
    const convertedNumericMetricsData = applyConversionFormula(combinedTimeSeriesData, dataSource.metricsMetadata);


    // Merge all the timeSeriesData into an array of MultiMetricDataPoint ordered by the data points' timestamp
    const mergedTimeSeriesData = mergeMetricDataToMultiMetricDataPoints(convertedNumericMetricsData);

    return {
        numericMetrics: fetchDataParams.succeedTimeSeriesIds.map((seriesId) => {
            return {
                name       : seriesId,
                displayName: dataSource.metricsMetadata?.[seriesId]?.displayName,
                unit       : dataSource.metricsMetadata?.[seriesId]?.unit,
            };
        }),
        numericMetricsData: mergedTimeSeriesData,

        // Use the first and last data point in the mergedMetricsData to get the data range
        dataRange: mergedTimeSeriesData.length > 0
            ? {
                start: mergedTimeSeriesData[0].dateValue,
                end  : mergedTimeSeriesData[mergedTimeSeriesData.length - 1].dateValue,
            } : {
                start: Date.now() - Time.DAY_IN_MS,
                end  : Date.now(),
            },
    };
};



/**
 * Create a MetricsMetadata object by merging NumericMetric/TagMetric metadata from entity MetricsDefinition with widget dataSource's metricsMetadata
 * @param metricsInfo NumericMetric or TagMetric
 */
const mergeMetricsMetadata = (metricInfo: NumericMetricInfo | TagMetricInfo, dataSource: EntityTSDBDashboardWidgetDataSource): MetricMetadata => {
    const metricName = metricInfo.name;

    // First, create metricMetadata object using settings from the metricsDefinition e.g. displayName and unit (for NumericMetric).
    // Entity's metricsDefinition only have those 2 attributes
    const metricMetadata = isNumericMetricInfo(metricInfo)
        ? {
            displayName: metricInfo.displayName,
            unit       : metricInfo.unit,
        } : {
            displayName: metricInfo.displayName,
        };

    // Then if dataSource.metricsMetadata also has metadata defined for the metricName, override the metricMetadata with
    // settings from dataSource.metricsMetadata. dataSource.metricsMetadata also have 'displayName', 'unit', and in addition to those,
    // it also have 'conversionFormula', 'lowValueWarningThreshold', 'highValueWarningThreshold', 'yAxisMin', 'yAxisMax', etc...
    const dataSourceMetricMetadata = dataSource.metricsMetadata?.[metricName];
    if (dataSourceMetricMetadata) {
        Object.entries(dataSourceMetricMetadata).forEach(([metadataName, metadataValue]) => {
            // Only override metadata that has value NOT undefined, null, or empty string
            if (!(metadataValue === undefined || metadataValue === null || metadataValue === '')) {
                metricMetadata[metadataName] = metadataValue;
            }
        });
    }

    return metricMetadata;
};


/**
 * Select COLLECTION data from the data state and use the provided fetchDataParams to find the data
 */
export const selectCollectionData = (
    appDataState: UserAppDataState,
    fetchDataParams: FetchAggregatedCollectionDataParams | FetchRawCollectionDataParams,
    dataSource: CollectionDashboardWidgetDataSource | EntityTSDBDashboardWidgetDataSource,
    entity?: Entity | undefined,
    metricsDefinition?: EntityMetricsDefinition | undefined,
): TSDashboardWidgetRequiredViewData | undefined => {

    // If we fetched data with a dummy value name then when we select data, we need to also include the dummy value name because the fetch
    // and select call params must match
    const fixedValueNames = fetchDataParams.valueNames && fetchDataParams.valueNames.length > 0
        ? fetchDataParams.valueNames : [DUMMY_VALUE_NAME];


    const collectionData = isFetchAggregatedCollectionDataParams(fetchDataParams)
        ? selectTimeSeriesCollectionData(appDataState, fetchDataParams.homeId, fetchDataParams.smartModuleId, fetchDataParams.collectionId,
            fetchDataParams.startTime, fetchDataParams.endTime, fixedValueNames, fetchDataParams.aggregation)
        : selectTimeSeriesCollectionRawData(appDataState, fetchDataParams.homeId, fetchDataParams.smartModuleId, fetchDataParams.collectionId,
            fetchDataParams.endTime, fetchDataParams.limit, fixedValueNames, fetchDataParams.tagNames || []);


    // If collectionData is not available, it is probably not done loading yet so return undefined and try again later
    if (!collectionData) {
        return undefined;
    }


    // Convert collection's data format from backend to a map of metricName => DataPoints array
    const reformattedNewData = convertCollectionDataToMetricsDataPoints(
        collectionData, fixedValueNames, isFetchRawCollectionDataParams(fetchDataParams) ? fetchDataParams.tagNames : undefined,
    );


    // Get the metricsMetadata from the dataSource if the dataSourceType is TSDB. If dataSourceType is ENTITY, the metadata will come
    // from the metricsDefinition. We need to build a metrics metadata object using the metricsDefinition.
    // But for Entity data sources, the user can also provide metricsMetadata to override the metadata from metricsDefinition for example
    // they can override the displayName or unit. We once we created the metricsMetadata using the metricsDefinition, we will check to
    // see if the dataSource also contain metricsMetadata. If it does then we will override the metadata from metricsDefinition with the
    // dataSource.metricsMetadata.
    const metricsMetadata = isEntityDashboardWidgetDataSource(dataSource) && metricsDefinition
        // dataSource type is ENTITY so use BOTH metadata from metricsDefinition AND dataSource.metricsMetadata
        ? {
            ...metricsDefinition.numericMetrics?.reduce((result, metricInfo: NumericMetricInfo): MetricsMetadata => {
                return {
                    ...result,
                    [metricInfo.name]: mergeMetricsMetadata(metricInfo, dataSource),
                };
            }, {}),
            ...metricsDefinition.tagMetrics?.reduce((result, metricInfo: TagMetricInfo): MetricsMetadata => {
                return {
                    ...result,
                    [metricInfo.name]: mergeMetricsMetadata(metricInfo, dataSource),
                };
            }, {}),
        }
        // dataSource type is TSDB so use metricsMetadata from dataSource
        : dataSource.metricsMetadata;



    // Call the helper function to apply conversion formula to the numeric metrics
    const convertedNumericMetricsData = applyConversionFormula(reformattedNewData.numericMetrics, metricsMetadata);


    // Get the numeric metric data from the reformatted data. However, if we fetched data with dummy value name earlier then this
    // numericMetricData data will be that dummy data which we don't need and can set numericMetricData as undefined.
    const numericMetricData = fetchDataParams.valueNames && fetchDataParams.valueNames.length > 0
        ? mergeMetricDataToMultiMetricDataPoints(convertedNumericMetricsData) : undefined;



    // get the tagMetricData from the reformattedNewData
    const tagMetricData = isFetchRawCollectionDataParams(fetchDataParams)
        ? mergeMetricDataToMultiMetricDataPoints(reformattedNewData.tagMetrics) : undefined;


    // Find the ABSOLUTE dateRange from all the data. This is the date of the VERY FIRST and VERY LAST data points. Because we have many
    // metrics, we need to look at all of them. First, use the first and last data point in the numericMetricData to get the data range
    const dateRange = numericMetricData && numericMetricData.length > 0
        ? {
            start: numericMetricData[0].dateValue,
            end  : numericMetricData[numericMetricData.length - 1].dateValue,
        } : {
            start: Number.MAX_SAFE_INTEGER,
            end  : Number.MIN_SAFE_INTEGER,
        };

    // If we have tagMetricData as well then use the tagMetricData's first and last data points if their value is better
    if (tagMetricData && tagMetricData.length > 0) {
        dateRange.start = Math.min(dateRange.start, tagMetricData[0].dateValue);
        dateRange.end = Math.max(dateRange.end, tagMetricData[tagMetricData.length - 1].dateValue);
    }


    // Now return the selected data in the format that the DataIntervalDashboardWidget can work with
    return {
        numericMetrics: fetchDataParams.valueNames && fetchDataParams.valueNames.length > 0
            ? fetchDataParams.valueNames.map((valueName): NumericMetricInfo => {
                return {
                    name       : valueName,
                    displayName: metricsMetadata?.[valueName]?.displayName,
                    unit       : metricsMetadata?.[valueName]?.unit,
                };
            }) : undefined,
        numericMetricsData: numericMetricData,
        tagMetrics        : isFetchRawCollectionDataParams(fetchDataParams) && fetchDataParams.tagNames && fetchDataParams.tagNames.length > 0
            ? fetchDataParams.tagNames.map((tagName): TagMetricInfo => {
                return {
                    name       : tagName,
                    displayName: metricsMetadata?.[tagName]?.displayName,
                };
            }) : undefined,
        tagMetricsData: tagMetricData,
        dataRange     : dateRange,
    };
};



/**
 * This is the main function for selecting fetched data from the data state. This function will call the appropriate sub function to select data
 * based on the widget settings.
 * @param dataState
 * @param dataSource
 * @param fetchDataParams
 */
export const selectTSData = (
    appDataState: UserAppDataState, dataSource: DataIntervalDashboardWidgetSettings['dataSource'],
    fetchDataParams: FetchAggregatedSimpleSeriesDataParams | FetchRawSimpleSeriesDataParams
    | FetchAggregatedCollectionDataParams | FetchRawCollectionDataParams,
    entity?: Entity | undefined,
    metricsDefinition?: EntityMetricsDefinition,
): TSDashboardWidgetRequiredViewData | undefined => {
    if (isSimpleSeriesDashboardWidgetDataSource(dataSource) && (
        isFetchAggregatedSimpleSeriesDataParams(fetchDataParams) || isFetchRawSimpleSeriesDataParams(fetchDataParams))) {
        return selectSimpleSeriesData(appDataState, fetchDataParams, dataSource);
    }
    if (isCollectionDashboardWidgetDataSource(dataSource) && (
        isFetchAggregatedCollectionDataParams(fetchDataParams) || isFetchRawCollectionDataParams(fetchDataParams))) {
        return selectCollectionData(appDataState, fetchDataParams, dataSource);
    }
    if (isEntityDashboardWidgetDataSource(dataSource) && (
        isFetchAggregatedCollectionDataParams(fetchDataParams) || isFetchRawCollectionDataParams(fetchDataParams))) {
        return selectCollectionData(appDataState, fetchDataParams, dataSource, entity, metricsDefinition);
    }
    return undefined;
};



/**
 * Validate the BASIC required inputs for a dashboard widget. This will only validate the basic inputs that all dashboard widget has e.g.
 * title, icon, etc... but not the inputs for more specific type of dashboard widget.
 * @param inputs
 */
export const validateBaseDashboardWidgetInputs = (
    inputs: DashboardWidgetInputs,
): DashboardWidgetInputErrors => {

    const { title } = inputs;
    const newInputErrors: DashboardWidgetInputErrors = {};

    if (!title) {
        newInputErrors.title = 'error.input.generic.empty';
    }

    return newInputErrors;
};


/**
 * Validate the inputs for dashboard widget type that has data
 * @param inputs
 * @returns
 */
export const validateDataDashboardWidgetInputs = (
    inputs: DataDashboardWidgetInputs,
): DataDashboardWidgetInputErrors => {

    const { reloadDataFreq } = inputs;
    const newInputErrors: DataDashboardWidgetInputErrors = {};

    if (reloadDataFreq === undefined || reloadDataFreq < 0) {
        newInputErrors.reloadDataFreq = 'error.input.generic.empty';
    }

    return newInputErrors;
};



/**
 * Validate the inputs for Time Series dashboard widget data source
 * @param inputs
 */
export const validateTSDashboardWidgetDataSourceInputs = (
    inputs: DataIntervalDashboardWidgetInputs,
): TSDBDashboardWidgetDataSourceInputErrors => {

    const { homeId, dataSourceType, smartModuleId, aggregationType, intervalType } = inputs;

    const newInputErrors: TSDBDashboardWidgetDataSourceInputErrors = {};

    // Then validate inputs required for timeSeries dashboard widgets
    if (!homeId) {
        newInputErrors.homeId = 'error.input.generic.empty';
    }

    if (!dataSourceType) {
        newInputErrors.dataSourceType = 'error.input.generic.empty';
    }

    if (!smartModuleId) {
        newInputErrors.smartModuleId = 'error.input.generic.empty';
    }

    if (!aggregationType) {
        newInputErrors.aggregationType = 'error.input.generic.empty';
    }

    if (!intervalType) {
        newInputErrors.intervalType = 'error.input.generic.empty';
    }


    // NOTE: some options don't go together for example, aggregated data can't be used with raw data interval and raw data can't be
    // use with aggregated data interval so we need to check for these conflict of settings
    if (intervalType && Object.values(AggregatedDataIntervalType).includes(intervalType as AggregatedDataIntervalType)) {
        // If intervalType is an AggregatedDataIntervalType e.g. 'Last 3 days', 'Last 90 days', 'Daily', 'Hourly', etc... then make sure
        // aggregationType is one of the one of the aggregation type e.g. 'avg', 'avg', etc... If it is not then show error.
        if (aggregationType && aggregationType === NoneValue) {
            newInputErrors.aggregationType = 'error.input.intervalType.conflict_with_raw_data';
            newInputErrors.intervalType = 'error.input.intervalType.conflict_with_raw_data';
        }
    } else if (intervalType && Object.values(RawDataIntervalType).includes(intervalType as RawDataIntervalType)) {
        // If intervalType is an RawDataIntervalType e.g. 'Last 100 values', 'Last 500 values', etc... then make sure aggregationType
        // is ValueNone. If it is not ValueNone then show error.
        if (aggregationType && aggregationType !== NoneValue) {
            newInputErrors.aggregationType = 'error.input.intervalType.conflict_with_aggregated_data';
            newInputErrors.intervalType = 'error.input.intervalType.conflict_with_aggregated_data';
        }
    }


    return newInputErrors;
};



/**
 * Validate the inputs for SIMPLE Series dashboard widget data source
 * @param inputs
 */
export const validateSimpleSeriesDashboardWidgetDataSourceInputs = (
    inputs: TSDBDashboardWidgetDataSourceInputs,
): TSDBDashboardWidgetDataSourceInputErrors => {

    const { seriesIds } = inputs;

    // First call validateTimeSeriesDashboardWidgetInputs to validate the inputs required for time series dashboard widget
    const newInputErrors: TSDBDashboardWidgetDataSourceInputErrors = validateTSDashboardWidgetDataSourceInputs(inputs);

    // Then validate inputs required for simple series dashboard widgets
    if (!seriesIds || seriesIds.length === 0) {
        newInputErrors.seriesIds = 'error.input.generic.empty';
    }

    return newInputErrors;
};



/**
 * Validate the inputs for COLLECTION dashboard widget data source
 * @param inputs
 */
export const validateCollectionDashboardWidgetDataSourceInputs = (
    inputs: TSDBDashboardWidgetDataSourceInputs,
): TSDBDashboardWidgetDataSourceInputErrors => {

    const { collectionId, valueNames, tagNames, aggregationType } = inputs;

    // First call validateTimeSeriesDashboardWidgetInputs to validate the inputs required for time series dashboard widget
    const newInputErrors: TSDBDashboardWidgetDataSourceInputErrors = validateTSDashboardWidgetDataSourceInputs(inputs);

    // Then validate inputs required for collection dashboard widgets
    if (!collectionId) {
        newInputErrors.collectionId = 'error.input.generic.empty';
    }

    // valueNames and tagNames are NOT BOTH required. The agent must provide values for either one or both but not none. Therefore, we will
    // only return errors if BOTH are not provided. If one of them is not empty then everything is good.
    if ((!valueNames || valueNames.length === 0) && (!tagNames || tagNames.length === 0)) {
        newInputErrors.valueNames = 'error.input.generic.empty';
        newInputErrors.tagNames = 'error.input.generic.empty';
    }

    // NOTE: some options don't go together for example, AGGREGATION can't be used with Tag metrics so we need to check for these inputs
    // that can't be used together, not just checking for missing inputs.

    // If aggregationType is set to some type that is NOT ValueNone e.g. 'avg', 'sum', 'min', etc... then make sure no tag metrics is selected
    // because we can't fetch aggregated data for tag metrics. If tag metrics is selected then show error.
    if (aggregationType && aggregationType !== NoneValue && inputs.tagNames && inputs.tagNames.length > 0) {
        newInputErrors.aggregationType = 'error.input.aggregationType.conflict_with_tag_metrics';
        newInputErrors.tagNames = 'error.input.aggregationType.conflict_with_tag_metrics';
    }

    return newInputErrors;
};



/**
 * Validate the inputs for ENTITY dashboard widget data source
 * @param inputs
 */
export const validateEntityDashboardWidgetDataSourceInputs = (
    inputs: TSDBDashboardWidgetDataSourceInputs,
): TSDBDashboardWidgetDataSourceInputErrors => {

    const { entity, metricsDefinition, valueNames, tagNames, aggregationType } = inputs;

    // First call validateTimeSeriesDashboardWidgetInputs to validate the inputs required for time series dashboard widget
    // Because ENTITY doesn't ask the user to provide 'smartModeId' since 'smartModuleId' is already defined in metricsDefinition,
    // we will add this values to the inputs so that the 'validateTSDashboardWidgetDataSourceInputs' won't complain about those missing inputs.
    const newInputErrors: TSDBDashboardWidgetDataSourceInputErrors = validateTSDashboardWidgetDataSourceInputs({
        ...inputs,
        smartModuleId: inputs.metricsDefinition?.dataStoreConfiguration.moduleInstanceId,
    });

    // Make sure entity and metricsDefinition are selected
    if (!entity) {
        newInputErrors.entity = 'error.input.generic.empty';
    }

    if (!metricsDefinition) {
        newInputErrors.metricsDefinition = 'error.input.generic.empty';
    }

    // valueNames and tagNames are NOT BOTH required. The agent must provide values for either one or both but not none. Therefore, we will
    // only return errors if BOTH are not provided. If one of them is not empty then everything is good.
    if ((!valueNames || valueNames.length === 0) && (!tagNames || tagNames.length === 0)) {
        newInputErrors.valueNames = 'error.input.generic.empty';
        newInputErrors.tagNames = 'error.input.generic.empty';
    }

    // NOTE: some options don't go together for example, AGGREGATION can't be used with Tag metrics so we need to check for these inputs
    // that can't be used together, not just checking for missing inputs.

    // If aggregationType is set to some type that is NOT ValueNone e.g. 'avg', 'sum', 'min', etc... then make sure no tag metrics is selected
    // because we can't fetch aggregated data for tag metrics. If tag metrics is selected then show error.
    if (aggregationType && aggregationType !== NoneValue && inputs.tagNames && inputs.tagNames.length > 0) {
        newInputErrors.aggregationType = 'error.input.aggregationType.conflict_with_tag_metrics';
        newInputErrors.tagNames = 'error.input.aggregationType.conflict_with_tag_metrics';
    }

    return newInputErrors;
};



/**
 * Validate the metric metadata conversion formula inputs ONLY IF they are provided because conversion formulas are OPTIONAL.
 * Return an object of errors for EACH metric that contain invalid conversion formula.
 *
 * @param metricNames
 * @param inputs
 * @returns
 */
export const validateMetricsConversionFormulaInputs = (
    metricNames: readonly string[],
    inputs: TSDBDashboardWidgetDataSourceInputs,
): DataIntervalDashboardWidgetInputErrors['metricsMetadata'] | undefined => {

    if (!inputs.metricsMetadata) {
        // Conversion formula is OPTIONAL therefore No metadata means no errors
        return undefined;
    }

    const metricsConversionFormulaErrors: DataIntervalDashboardWidgetInputErrors['metricsMetadata'] = metricNames.reduce((result, metricName) => {
        // Get metricMetadata inputs for ONE metric
        const metadataInputs = inputs.metricsMetadata?.[metricName];
        if (metadataInputs?.conversionFormula) {
            // Check if the conversionFormula is valid
            try {
                // Dynamically create a Function which takes 'value' as param and using the metadataInputs.conversionFormula string
                // as function body. NOTE: metadataInputs.conversionFormula is just a formula, it does not have a 'return' statement
                // therefore we must add 'return' to the string
                // eslint-disable-next-line no-new-func
                const func = new Function('value', `return (${metadataInputs.conversionFormula})`);

                // Try to call the function a few time with some values to see if it work.
                func(-1);
                func(0);
                func(1);

            } catch (error) {
                return {
                    ...result,
                    [metricName]: (error as Error).message,
                };
            }
        }
        return result;
    }, {});


    // Go through all the values in the metricsConversionFormulaErrors object to find a non undefined value. If there is at least 1 non
    // undefined value, that mean one of the metrics have invalid conversion formula settings.
    const hasMetricConversionFormulaErrors = Object.values(metricsConversionFormulaErrors).some((error) => {
        return error !== undefined;
    });

    if (hasMetricConversionFormulaErrors) {
        return metricsConversionFormulaErrors;
    }

    return undefined;
};



/**
 * Validate metric metadata's threshold inputs.
 * Return an object of errors, one for EACH metric that contain invalid or missing threshold input.
 * @param metricNames
 * @param inputs
 * @returns
 */
export const validateMetricsThresholdInputs = (
    metricNames: readonly string[],
    inputs: TSDBDashboardWidgetDataSourceInputs,
): DataIntervalDashboardWidgetInputErrors['metricsMetadata'] | undefined => {

    const missingMetadataInputErrorMessage = 'error.input.metric_metadata.thresholds.empty';

    if (!inputs.metricsMetadata) {
        /**
         * If metricsMetadata is not provided at all, set this error for EACH metric name e.g.
         *
         *      return {
         *          temperature: "error message",
         *          pressure: "error message",
         *          humidity: "error message"
         *      }
         * */
        return metricNames.reduce((result, metricName) => {
            return {
                ...result, [metricName]: missingMetadataInputErrorMessage,
            };
        }, {});
    }

    /**
     * If 'metricsMetadata' input is NOT null then go through EACH measurement name in the metricsMetadata input and make sure they all
     * have at least 1 threshold setting each. For the metric that DON'T have threshold setting, add error message for that metric. E.g.
     *
     *      return {
     *          temperature: undefined,     // If temperature's metricMetadata threshold is set, no error
     *          pressure: "error message",  // If pressure and humidity metrics' metadata threshold are not set then they have error
     *          humidity: "error message",
     *      }
     */
    const metricsThresholdErrors: TSDBDashboardWidgetDataSourceInputErrors['metricsMetadata'] = metricNames.reduce((result, metricName) => {
        // Get metricMetadata inputs for ONE metric
        const metadataInputs = inputs.metricsMetadata?.[metricName];

        if (!metadataInputs || (
            metadataInputs.lowValueWarningThreshold === undefined
                    && metadataInputs.lowValueCriticalThreshold === undefined
                    && metadataInputs.highValueWarningThreshold === undefined
                    && metadataInputs.highValueCriticalThreshold === undefined
        )) {
            // If there is no threshold setting for the current metricName then add error message for this metricName
            // We only need to check the threshold value and can ignore the minValue, mavValue, and other metadata not required
            // for 'monitorValueThresholds'
            return {
                ...result,
                [metricName]: missingMetadataInputErrorMessage,
            };
        } if (metadataInputs) {
            // If threshold settings are set, make sure the values are valid. We need to make sure the
            // LOW_CRITICAL < LOW_WARNING < HIGH_WARNING < HIGH_CRITICAL values
            // NOTE: not all these values are set so we only need to check the one that are set

            // first, create an array of threshold values in the order that they need to be and then filter out the 'undefined' values
            const thresholdValues = [
                metadataInputs.lowValueCriticalThreshold,
                metadataInputs.lowValueWarningThreshold,
                metadataInputs.highValueWarningThreshold,
                metadataInputs.highValueCriticalThreshold,
            ].filter((value) => {
                return value !== undefined;
            });

            // Now check if the values are in ascending order. If they are not then that mean one of the values is invalid
            const badValueIndex = thresholdValues.findIndex((value, index) => {
                // if the previous value is >= than the current value then the values are not in ascending order
                if (index > 0) {
                    const previousValue = thresholdValues[index - 1];
                    if (previousValue !== undefined && value !== undefined && previousValue >= value) {
                        // we found a bad value, previousValue SHOULD NOT be greater or equal to current value
                        return true;
                    }
                }
                return false;
            });

            if (badValueIndex >= 0) {
                return {
                    ...result,
                    [metricName]: 'error.input.metric_metadata.thresholds.invalid',
                };
            }
        }
        return result;
    }, {});


    // Go through all the values in the metricsThresholdErrors object to find a non undefined value. If there is at least 1 non
    // undefined value, that mean one of the metrics does not have threshold settings.
    const hasMetricThresholdErrors = Object.values(metricsThresholdErrors).some((error) => {
        return error !== undefined;
    });

    if (hasMetricThresholdErrors) {
        return metricsThresholdErrors;
    }

    return undefined;
};


/**
 * Validate metric metadata's threshold inputs for decimal places.
 * Return an object of errors, one for EACH metric that contain invalid or missing threshold input.
 * @param inputs
 * @returns
 */
export const validateDecimalPlacesInputs = (
    metricNames: readonly string[],
    inputs: TSDBDashboardWidgetDataSourceInputs,
    trans: TFunction,
): TSDBDashboardWidgetDataSourceInputErrors['metricsMetadata'] | undefined => {

    if (!inputs.metricsMetadata) {
        // Decimal Places is OPTIONAL therefore No metadata means no errors
        return undefined;
    }

    const decimalPlacesErrorMessage = trans('error.input.metric_metadata.decimal_places.invalid', {
        min: MIN_DECIMAL_PLACES, max: MAX_DECIMAL_PLACES,
    });


    const decimalPlacesErrors: TSDBDashboardWidgetDataSourceInputErrors['metricsMetadata'] = metricNames.reduce((result, metricName) => {
        // Get metricMetadata inputs for ONE metric
        const metadataInputs = inputs.metricsMetadata?.[metricName];

        // check to make sure that the input is valid, if not throw error
        if (metadataInputs?.decimalPlaces !== undefined) {
            if (!(Number.isInteger(metadataInputs.decimalPlaces)
            && metadataInputs.decimalPlaces >= MIN_DECIMAL_PLACES && metadataInputs.decimalPlaces <= MAX_DECIMAL_PLACES)) {
                return {
                    ...result,
                    [metricName]: decimalPlacesErrorMessage,
                };
            }

        }
        return result;
    }, {});

    // Go through all the values in thedecimalPlacesErrors object to find a non undefined value. If there is at least 1 non
    // undefined value, that mean one of the metrics have invalid decimal places input.
    const hasDecimalPlacesErrors = Object.values(decimalPlacesErrors).some((error) => {
        return error !== undefined;
    });

    if (hasDecimalPlacesErrors) {
        return decimalPlacesErrors;
    }

    return undefined;
};


/**
 * Validate the view's metricsMetadataMetric input. For now, we will only make sure the yAxisMin < yAxisMax if they are provided
 * @param metricNames
 * @param inputs
 * @returns
 */
export const validateViewMetricsMetadataInputs = (
    metricNames: readonly string[],
    inputs: DashboardWidgetViewSettingsInputs,
): DashboardWidgetViewSettingsInputErrors['viewMetricsMetadata'] | undefined => {

    const invalidMetadataInputErrorMessage = 'error.input.yAxisMinMax.invalid';

    // viewMetricsMetadata is OPTIONAL therefore if it is not defined, it is ok, no error
    if (!inputs.viewMetricsMetadata) {
        return undefined;
    }

    /**
     * If 'viewMetricsMetadata' input is NOT null then go through EACH measurement name in the viewMetricsMetadata input and make sure if
     * anyone of them have BOTH yAxisMin and yAxisMax set then yAxisMin MUST be less than yAxisMax. If only 1 value is provided then
     * don't need to check.
     *
     *      return {
     *          temperature: undefined,     // If temperature's metricMetadata threshold is set, no error
     *          pressure: "error message",  // If pressure and humidity metrics' metadata has invalid yAxisMin and yAxisMax set
     *          humidity: "error message",
     *      }
     */
    const metricsErrors: DashboardWidgetViewSettingsInputErrors['viewMetricsMetadata'] = metricNames.reduce((result, metricName) => {
        // Get metricMetadata inputs for ONE metric
        const metadataInputs = inputs.viewMetricsMetadata?.[metricName];
        if (metadataInputs?.yAxisMin !== undefined && metadataInputs?.yAxisMax !== undefined && metadataInputs.yAxisMin >= metadataInputs.yAxisMax) {
            // if BOTH yAxisMin and yAxisMax are provided but yAxisMin >= yAxisMax then there error
            return {
                ...result,
                [metricName]: invalidMetadataInputErrorMessage,
            };
        }

        return result;
    }, {});


    // Go through all the values in the metricsThresholdErrors object to find a non undefined value. If there is at least 1 non
    // undefined value, that mean one of the metrics does not have threshold settings.
    const hasMetricThresholdErrors = Object.values(metricsErrors).some((error) => {
        return error !== undefined;
    });

    if (hasMetricThresholdErrors) {
        return metricsErrors;
    }

    return undefined;
};



/**
 * Validate the inputs for DATA SOURCE
 */
export const validateDataSourceInputs = (inputs: TSDBDashboardWidgetDataSourceInputs): TSDBDashboardWidgetDataSourceInputErrors => {
    const { dataSourceType } = inputs;
    if (dataSourceType === TSDBDataSourceType.SIMPLE) {
        return validateSimpleSeriesDashboardWidgetDataSourceInputs(inputs);
    }
    if (dataSourceType === TSDBDataSourceType.COLLECTION) {
        return validateCollectionDashboardWidgetDataSourceInputs(inputs);
    }
    // dataSourceType must be ENTITY
    return validateEntityDashboardWidgetDataSourceInputs(inputs);
};


/**
 * Validate the inputs for SPECIFIC dashboard widget of type DataIntervalDashboardWidget
 */
export const validateDataIntervalDashboardWidgetInputs = (
    inputs: DataIntervalDashboardWidgetInputs,
    trans: TFunction,
): DataIntervalDashboardWidgetInputErrors => {

    const { viewType, viewMetricsMetadata } = inputs;

    // validate the basic dashboard widget inputs e.g. title, icon, etc...
    const basicDashboardWidgetInputErrors: DashboardWidgetInputErrors = validateBaseDashboardWidgetInputs(inputs);

    // validate the data dashboard widget inputs e.g. reloadInterval
    const dataDashboardWidgetInputErrors: DataDashboardWidgetInputErrors = validateDataDashboardWidgetInputs(inputs);

    // Validate the data source inputs and get the dataSourceInputErrors
    const dataSourceInputErrors: TSDBDashboardWidgetDataSourceInputErrors = validateDataSourceInputs(inputs);


    // Then validate inputs required for time series dashboard widgets.
    const viewInputErrors: DataIntervalDashboardWidgetViewSettingsInputErrors = {};

    if (!viewType) {
        viewInputErrors.viewType = 'error.input.generic.empty';
    } else if (viewType === DataIntervalViewType.GANTT_CHART) {
        // GANTT_CHART can only be used for tagMetrics so if the dataSource contain numeric metrics then return error
        if (inputs.dataSourceType === TSDBDataSourceType.SIMPLE) {
            // If dataSource.dataSourceType is SIMPLE time series then we can assume it is numericMetrics and show error
            dataSourceInputErrors.seriesIds = 'error.input.viewType.gantt_chart_conflict_with_numeric_metrics';
            viewInputErrors.viewType = 'error.input.viewType.gantt_chart_conflict_with_numeric_metrics';
        } else if (inputs.valueNames && inputs.valueNames.length > 0) {
            // if dataSource.dataSourceType is COLLECTION or ENTITY and if it has 'valueNames' (numericMetrics) then also show error
            dataSourceInputErrors.valueNames = 'error.input.viewType.gantt_chart_conflict_with_numeric_metrics';
            viewInputErrors.viewType = 'error.input.viewType.gantt_chart_conflict_with_numeric_metrics';
        }
    } else if (
        viewType === DataIntervalViewType.LINE_CHART || viewType === DataIntervalViewType.BAR_CHART || viewType === DataIntervalViewType.AREA_CHART
    ) {
        // XYCharts only work with numeric metrics, opposite to Gantt Chart, so we can't allow tag metrics
        if (inputs.dataSourceType !== TSDBDataSourceType.SIMPLE && inputs.tagNames && inputs.tagNames.length > 0) {
            dataSourceInputErrors.tagNames = 'error.input.viewType.line_bar_chart_conflict_with_tag_metrics';
            viewInputErrors.viewType = 'error.input.viewType.line_bar_chart_conflict_with_tag_metrics';
        }
    }


    // Get all the metrics' name based on the data source settings. This should be either the list of seriesIds or
    // the list of valueNames + tagNames
    const metricNames = inputs.dataSourceType === TSDBDataSourceType.SIMPLE
        ? inputs.seriesIds || []
        : [...(inputs.valueNames || []), ...(inputs.tagNames || [])];


    // If viewMetricsMetadata is provided then validate it. We need to make sure yAxisMin and yAxisMax inputs for each metrics are valid
    if (viewMetricsMetadata) {
        const viewMetricsMetadataErrors = validateViewMetricsMetadataInputs(metricNames, inputs);

        // Set the errors to the viewMetricsMetadata
        viewInputErrors.viewMetricsMetadata = viewMetricsMetadataErrors;
    }

    // Validate the decimalPlaces input and grab errors
    const decimalPlacesInputErrors = validateDecimalPlacesInputs(metricNames, inputs, trans);
    if (decimalPlacesInputErrors) {
        dataSourceInputErrors.metricsMetadata = decimalPlacesInputErrors;
    }


    // call helper function to validate metric metadata's conversion formula
    const metricsConversionFormulaErrors = validateMetricsConversionFormulaInputs(metricNames, inputs);
    if (metricsConversionFormulaErrors) {
        dataSourceInputErrors.metricsMetadata = metricsConversionFormulaErrors;
    }


    // Merge the base dashboard widget input errors and the specific input errors for this widget type
    return {
        ...basicDashboardWidgetInputErrors,
        ...dataDashboardWidgetInputErrors,
        ...dataSourceInputErrors,
        ...viewInputErrors,
    };
};



/**
 * Validate the inputs for SPECIFIC dashboard widget of type ValueBoardWidget
 *
 * @param inputs
 */
export const validateValueDashboardWidgetInputs = (
    inputs: ValueDashboardWidgetInputs,
    trans: TFunction,
): ValueDashboardWidgetInputErrors => {

    // validate the basic dashboard widget inputs e.g. title, icon, etc...
    const basicDashboardWidgetInputErrors: DashboardWidgetInputErrors = validateBaseDashboardWidgetInputs(inputs);

    // validate the data dashboard widget inputs e.g. reloadInterval
    const dataDashboardWidgetInputErrors: DataDashboardWidgetInputErrors = validateDataDashboardWidgetInputs(inputs);

    // Validate the data source inputs and get the dataSourceInputErrors
    const dataSourceInputErrors: TSDBDashboardWidgetDataSourceInputErrors = validateDataSourceInputs(inputs);

    // Then validate inputs required for time series dashboard widgets.
    const viewInputErrors: ValueDashboardWidgetViewSettingsInputErrors = {};
    if (!inputs.viewType) {
        viewInputErrors.viewType = 'error.input.generic.empty';
    }
    if (!inputs.viewLayout) {
        viewInputErrors.viewLayout = 'error.input.generic.empty';
    }

    // Get all the metrics' name based on the data source settings. This should be either the list of seriesIds or
    // the list of valueNames + tagNames
    const metricNames = inputs.dataSourceType === TSDBDataSourceType.SIMPLE
        ? inputs.seriesIds || []
        : [...(inputs.valueNames || []), ...(inputs.tagNames || [])];

    // If the user enabled 'monitorValueThreshold', make sure 'inputs.metricMetadata' is configured correctly. The user needs to provide
    // at least one of the thresholds value
    if (inputs.monitorValueThresholds) {

        // Call helper function to validate the the input for metric threshold settings
        const metricsThresholdErrors = validateMetricsThresholdInputs(metricNames, inputs);
        if (metricsThresholdErrors) {
            // Set the errors to the dataSourceInputErrors
            dataSourceInputErrors.metricsMetadata = metricsThresholdErrors;

            // Also show an error for the 'monitorValueThresholds' input. Since there can be more than 1 threshold setting errors,
            // we will just going to tell the user that one of the threshold value is missing or invalid
            viewInputErrors.monitorValueThresholds = 'error.input.monitor_value_thresholds.invalid';
        }
    }


    // If viewMetricsMetadata is provided then validate it. We need to make sure yAxisMin and yAxisMax inputs for each metrics are valid
    if (inputs.viewMetricsMetadata) {
        const viewMetricsMetadataErrors = validateViewMetricsMetadataInputs(metricNames, inputs);

        // Set the errors to the viewMetricsMetadata
        viewInputErrors.viewMetricsMetadata = viewMetricsMetadataErrors;
    }

    // call helper function to validate metric metadata's conversion formula
    const metricsConversionFormulaErrors = validateMetricsConversionFormulaInputs(metricNames, inputs);
    if (metricsConversionFormulaErrors) {
        dataSourceInputErrors.metricsMetadata = metricsConversionFormulaErrors;
    }

    // Validate the decimalPlaces input and grab errors
    const decimalPlacesInputErrors = validateDecimalPlacesInputs(metricNames, inputs, trans);
    if (decimalPlacesInputErrors) {
        dataSourceInputErrors.metricsMetadata = decimalPlacesInputErrors;
    }

    // Merge the base dashboard widget input errors and the specific input errors for this widget type
    return {
        ...basicDashboardWidgetInputErrors,
        ...dataDashboardWidgetInputErrors,
        ...dataSourceInputErrors,
        ...viewInputErrors,
    };
};



export const validateHeatMapDashboardWidgetInputs = (
    inputs: HeatMapDashboardWidgetInputs,
): HeatMapDashboardWidgetInputErrors => {

    // validate the basic dashboard widget inputs e.g. title, icon, etc...
    const basicDashboardWidgetInputErrors: DashboardWidgetInputErrors = validateBaseDashboardWidgetInputs(inputs);

    // validate the data dashboard widget inputs e.g. reloadInterval
    const dataDashboardWidgetInputErrors: DataDashboardWidgetInputErrors = validateDataDashboardWidgetInputs(inputs);

    // Validate the data source inputs and get the dataSourceInputErrors
    const dataSourceInputErrors: TSDBDashboardWidgetDataSourceInputErrors = validateDataSourceInputs(inputs);


    // Then validate inputs required for time series dashboard widgets.
    const viewInputErrors: HeatMapDashboardWidgetInputErrors = {};

    if (!inputs.viewLayout) {
        viewInputErrors.viewLayout = 'error.input.generic.empty';
    }


    // Heat Map widget will ALWAYS require threshold input unlike Value widget so we need to validate the thresholds

    // Get all the metrics' name based on the data source settings. This should be either the list of seriesIds or
    // the list of valueNames + tagNames
    const metricNames = inputs.dataSourceType === TSDBDataSourceType.SIMPLE
        ? inputs.seriesIds || []
        : [...(inputs.valueNames || []), ...(inputs.tagNames || [])];


    // Call helper function to validate the the input for metric threshold settings
    const metricsThresholdErrors = validateMetricsThresholdInputs(metricNames, inputs);
    if (metricsThresholdErrors) {
        // Set the errors to the dataSourceInputErrors
        dataSourceInputErrors.metricsMetadata = metricsThresholdErrors;
    }


    // call helper function to validate metric metadata's conversion formula
    const metricsConversionFormulaErrors = validateMetricsConversionFormulaInputs(metricNames, inputs);
    if (metricsConversionFormulaErrors) {
        dataSourceInputErrors.metricsMetadata = metricsConversionFormulaErrors;
    }


    // Merge the base dashboard widget input errors and the specific input errors for this widget type
    return {
        ...basicDashboardWidgetInputErrors,
        ...dataDashboardWidgetInputErrors,
        ...dataSourceInputErrors,
        ...viewInputErrors,
    };
};


/**
 * Validate inputs for AlertsDashboardWidget
 */
export const validateAlertsDashboardWidgetInputs = (
    inputs: AlertsDashboardWidgetInputs,
): AlertsDashboardWidgetInputErrors => {

    // validate the basic dashboard widget inputs e.g. title, icon, etc...
    const basicDashboardWidgetInputErrors: DashboardWidgetInputErrors = validateBaseDashboardWidgetInputs(inputs);

    // Then validate inputs required for Alerts dashboard widget
    const alertWidgetInputErrors: AlertsDashboardWidgetInputErrors = {};
    if (!inputs.viewType) {
        alertWidgetInputErrors.viewType = 'error.input.generic.empty';
    }

    // Merge the base dashboard widget input errors and the specific input errors for this widget type
    return {
        ...basicDashboardWidgetInputErrors,
        ...alertWidgetInputErrors,
    };
};



/**
 * Validate inputs for EntitiesMapDashboardWidget
 */
export const validateEntitiesMapDashboardWidgetInputs = (
    inputs: EntitiesMapDashboardWidgetInputs,
): EntitiesMapDashboardWidgetInputErrors => {

    // validate the basic dashboard widget inputs e.g. title, icon, etc...
    const basicDashboardWidgetInputErrors: DashboardWidgetInputErrors = validateBaseDashboardWidgetInputs(inputs);

    // Then validate inputs required for Entities Map dashboard widget
    const entitiesMapWidgetInputErrors: EntitiesMapDashboardWidgetInputErrors = {};

    // entityClasses and entityIds are OPTIONAL but at least 1 of them must have some value. If both are empty then show error
    if ((!inputs.entityClasses || inputs.entityClasses?.length === 0) && (!inputs.entities || inputs.entities?.length === 0)) {
        entitiesMapWidgetInputErrors.entityClasses = 'error.input.generic.empty';
        entitiesMapWidgetInputErrors.entities = 'error.input.generic.empty';
    }

    // Merge the base dashboard widget input errors and the specific input errors for this widget type
    return {
        ...basicDashboardWidgetInputErrors,
        ...entitiesMapWidgetInputErrors,
    };
};
